import PropTypes from "prop-types"
import React from "react"
import { Controller, useForm } from "react-hook-form"
import { useMutation } from "react-query"

import Form from "src/components/Form"
import Modal from "src/components/Modal"
import TurnstileField from "src/components/Turnstile"

import { requestFuelPrices } from "src/api/MarinaProfile"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

const RequestFuelPricesModal = ({
  isOpen,
  handleClose,
  trackingProperties,
  fuelTypes,
  marinaId,
  setRecentRequestActive,
}) => {
  const tracker = useTracker()
  const showToast = useToast()
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
  } = useForm({
    defaultValues: {
      fuelType: "",
      turnstileToken: "",
    },
  })

  const handleRequestSuccess = (data) => {
    setRecentRequestActive(true)
    showToast(data.message, {
      type: "success",
    })
    handleClose()
  }

  const handleRequestError = (error) => {
    showToast(error.message, {
      type: "error",
    })
  }

  const { mutate: requestFuelPricesMutation, isLoading: requestingFuelPrice } =
    useMutation((data) => requestFuelPrices({ data }), {
      onSuccess: handleRequestSuccess,
      onError: handleRequestError,
    })

  const onSubmit = (data) => {
    tracker.trackEvent(
      "destination_detail:request_fuel_prices_clicked",
      trackingProperties
    )

    const formattedData = {
      fuel_type: data.fuelType || null,
      marina_id: marinaId,
      turnstile_token: data.turnstileToken,
    }

    requestFuelPricesMutation(formattedData)
  }

  const renderFuelTypeOptions = () => {
    return fuelTypes.map((fuelType) => (
      <option key={`fuelTypeRequest_${fuelType}`} value={fuelType}>
        {fuelType}
      </option>
    ))
  }

  const renderFuelTypeSelect = () => {
    return (
      <div>
        <Form.Label htmlFor="fuelTypes" optional>
          What type of fuel are you looking for?
        </Form.Label>
        <Form.Select {...register("fuelType")} id="fuelTypes">
          <option value="" disabled>
            Select a fuel type
          </option>
          {renderFuelTypeOptions()}
        </Form.Select>
      </div>
    )
  }

  const getConfirmButtonText = () => {
    return requestingFuelPrice ? "Requesting" : "Request updated prices"
  }

  const handleTurnstileVerification = (value) => {
    const newTrackingProperties = { ...trackingProperties }
    if (value === "error") {
      setError("turnstileToken", {
        type: "manual",
        message: "Turnstile verification failed. Please try again.",
      })
      newTrackingProperties.turnstileError = true
    } else if (value === "expired") {
      setError("turnstileToken", {
        type: "manual",
        message: "Turnstile verification expired. Please try again.",
      })
      newTrackingProperties.turnstileExpired = true
    } else {
      setValue("turnstileToken", value)
      newTrackingProperties.turnstileSuccess = true
      clearErrors("turnstileToken")
    }

    tracker.trackEvent(
      "destination_detail:request_fuel_prices:turnstile_verification",
      newTrackingProperties
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header title="Request updated fuel prices" />

        <Modal.Body>
          <div className="space-y-4 pb-4">
            <span>
              Let {trackingProperties.marinaName} know that their fuel prices
              may be missing or out of date.
            </span>

            {renderFuelTypeSelect()}
          </div>
          <Controller
            name="turnstileToken"
            control={control}
            rules={{
              required: "Verification is required",
            }}
            render={() => (
              <TurnstileField onChange={handleTurnstileVerification} />
            )}
          />
          {errors?.turnstileToken && (
            <Form.Error>{errors.turnstileToken.message}</Form.Error>
          )}
        </Modal.Body>
        <Modal.Footer
          confirmBtnText={getConfirmButtonText()}
          confirmBtnType="submit"
          onClose={handleClose}
          confirmBtnLoading={requestingFuelPrice}
        />
      </Form>
    </Modal>
  )
}

RequestFuelPricesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  trackingProperties: PropTypes.shape({
    userId: PropTypes.number,
    mcomId: PropTypes.string.isRequired,
    marinaName: PropTypes.string.isRequired,
  }),
  fuelTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  marinaId: PropTypes.number.isRequired,
  setRecentRequestActive: PropTypes.func.isRequired,
}

export default RequestFuelPricesModal
